import React, { useCallback, useEffect, useRef, useState } from 'react';
import PriceBadge from '../../../components/PriceBadge/PriceBadge';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { selectAssetFolderActionsPending } from '../../../store/slices/assetFolder/slice';
import { notifyError, notifyInfo } from '../../../utils/notify';
import PreviewAlbumMediaModal from '../../../components/modals/PreviewAlbumMediaModal/PreviewAlbumMediaModal';
import {
  AiSellContainer,
  Content,
  Header,
  HeaderButton,
  HeaderLeft,
  HeaderRight,
  HeaderRow,
  ListMedia,
  LoaderContainer,
  MediaCountText,
  MediaWrapper,
  NotValidMedia,
  PageContainer,
  PlayIconWrapper,
  SelectIconWrapper,
  StyledImage,
} from './UploadedFolderPage.styles';
import {
  CreateAssetFolderModal,
  DraggableMediaItem,
  EditAlbumMediaModal,
  SelectedItemsOptions,
  Spinner,
} from '../../../components';
import { Text14Light, Text18Bold } from '../../../components/utils/Text/Text.styles';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image-04.svg';
import { ReactComponent as VideoRecorderIcon } from '../../../assets/icons/video-recorder.svg';
import { ReactComponent as PlayIcon } from '../../../assets/icons/play-white.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/trash-1.svg';
import { ReactComponent as CheckCircleEmptyIcon } from '../../../assets/icons/check-circle-empty.svg';
import { ReactComponent as CheckCircleIcon } from '../../../assets/icons/check-circle-broken.svg';
import { ALBUM_MEDIA_TYPE, MEDIA_ASSET_SALE_TAG } from '../../../utils/constants';
import {
  changeAssetsMediaPositionFolder,
  deleteAssetFolder,
  fetchAssetFolderById,
  removeMediaFromAssetFolder,
} from '../../../store/slices/assetFolder/asyncThunks';
import { useConfirmationModal } from '../../../components/modals/ConfirmationModal/ConfirmationModal';
import { VAULT } from '../../../components/App/routes';
import { getAlbumMediaTags } from '../../../store/slices/influencerRequest/asyncThunks';

const isMediaNotValid = media => {
  if (media.tags?.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID)) {
    return !media.aiDescription || !media.fileId || !media.category;
  }

  if (media.tags?.includes(MEDIA_ASSET_SALE_TAG.BUNDLE)) {
    return !media.fileId;
  }

  return !media.fileId || !media.aiDescription;
};

const UploadedFolderPage = () => {
  const { folderId, influencerId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    fetchAssetFolderById: fetchAssetFolderByIdPending,
    deleteAssetFolder: deleteAssetFolderPending,
    removeMediaFromAssetFolder: removeMediaFromAssetFolderPending,
  } = useSelector(selectAssetFolderActionsPending);
  const previewAlbumMediaModalRef = useRef();
  const createAssetFolderModalRef = useRef();
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const editAlbumMediaModalRef = useRef();
  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  useEffect(() => {
    if (folderId) {
      dispatch(fetchAssetFolderById({ folderId, ...(influencerId && { influencerId }) }))
        .unwrap()
        .then(({ folder }) => {
          setSelectedFolder(folder);
        })
        .catch(err => {
          navigate(`/${VAULT}`);
          notifyError(err.message);
        });
    }
  }, [dispatch, navigate, folderId, influencerId]);

  useEffect(() => {
    dispatch(getAlbumMediaTags());
  }, [dispatch]);

  useEffect(() => {
    setConfirmationModalLoader(deleteAssetFolderPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteAssetFolderPending]);

  useEffect(() => {
    setConfirmationModalLoader(removeMediaFromAssetFolderPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMediaFromAssetFolderPending]);

  const onDeleteClick = () => {
    openModal(
      'Delete Folder',
      `Are you sure? This action will permanently delete the folder.`,
      'Delete',
      () => {
        dispatch(
          deleteAssetFolder({
            folderId: selectedFolder.id,
            ...(influencerId && { influencerId }),
          }),
        )
          .unwrap()
          .then(() => {
            closeModal();
            navigate(`${influencerId ? `/${influencerId}` : ''}/${VAULT}`);
            notifyInfo('Folder deleted successfully!');
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  const onEditClick = () => {
    createAssetFolderModalRef.current.show();
  };

  const onSelectItem = useCallback(media => {
    setSelectedItems(prev => {
      if (prev.find(m => m.id === media.id)) {
        return prev.filter(m => m.id !== media.id);
      } else {
        return [...prev, media];
      }
    });
  }, []);

  const onRemoveClick = () => {
    openModal(
      'Remove selected media',
      `This action will remove the selected media from the folder.`,
      'Remove',
      () => {
        dispatch(
          removeMediaFromAssetFolder({
            folderId: selectedFolder.id,
            media: selectedItems.map(item => ({ mediaId: item.id })),
            ...(influencerId && { influencerId }),
          }),
        )
          .unwrap()
          .then(() => {
            setSelectedItems([]);
            dispatch(fetchAssetFolderById({ folderId, ...(influencerId && { influencerId }) }))
              .unwrap()
              .then(({ folder }) => {
                setSelectedFolder(folder);
              })
              .catch(err => {
                notifyError(err.message);
              });
            closeModal();
            notifyInfo('Media removed from folder successfully!');
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  const onEditItemClick = useCallback(media => {
    setSelectedMedia(media);
    editAlbumMediaModalRef.current.show();
  }, []);

  const moveItem = (from, to) => {
    const updatedMedia = [...selectedFolder.media];
    const [movedMedia] = updatedMedia.splice(from, 1);
    updatedMedia.splice(to, 0, movedMedia);
    const mediaPositions = updatedMedia.map((media, index) => ({ id: media.id, position: index + 1 }));
    dispatch(changeAssetsMediaPositionFolder({ folderId, mediaPositions, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(() => {})
      .catch(err => {
        notifyError(err.message);
      });
  };

  const onDragging = (from, to) => {
    const updatedMedia = [...selectedFolder.media];
    const [movedMedia] = updatedMedia.splice(from, 1);
    updatedMedia.splice(to, 0, movedMedia);
    setSelectedFolder(prev => ({ ...prev, media: updatedMedia }));
  };

  return (
    <PageContainer>
      {fetchAssetFolderByIdPending ? (
        <LoaderContainer>
          <Spinner width={120} height={120} />
        </LoaderContainer>
      ) : (
        selectedFolder && (
          <Content>
            <Header>
              <HeaderLeft>
                <Text18Bold>{selectedFolder.name}</Text18Bold>
                <HeaderRow>
                  <ImageIcon color="#9DA4AE" height={18} width={18} />
                  <MediaCountText>
                    {selectedFolder.media.filter(m => m.kind === ALBUM_MEDIA_TYPE.PHOTO).length}
                  </MediaCountText>
                </HeaderRow>
                <HeaderRow>
                  <VideoRecorderIcon color="#9DA4AE" height={18} width={18} />
                  <MediaCountText>
                    {selectedFolder.media.filter(m => m.kind === ALBUM_MEDIA_TYPE.VIDEO).length}
                  </MediaCountText>
                </HeaderRow>
              </HeaderLeft>
              <HeaderRight>
                <HeaderButton onClick={onEditClick}>
                  <EditIcon />
                </HeaderButton>
                <HeaderButton onClick={onDeleteClick}>
                  <TrashIcon />
                </HeaderButton>
              </HeaderRight>
            </Header>
            <ListMedia>
              {selectedFolder.media.map((media, index) => {
                const notValid = isMediaNotValid(media);

                return (
                  <DraggableMediaItem key={media.id} index={index} moveItem={moveItem} onDragging={onDragging}>
                    <MediaWrapper
                      onClick={() => {
                        if (!notValid) {
                          setSelectedMedia(media);
                          previewAlbumMediaModalRef.current.show();
                        }
                      }}>
                      <StyledImage src={media.thumbnailUrl} />
                      <PriceBadge cost={media.cost} />
                      {notValid && (
                        <NotValidMedia>
                          <Text14Light>Processing, please wait...</Text14Light>
                        </NotValidMedia>
                      )}
                      {media.kind === ALBUM_MEDIA_TYPE.VIDEO && (
                        <PlayIconWrapper>
                          <PlayIcon fill="white" />
                        </PlayIconWrapper>
                      )}
                      <SelectIconWrapper
                        onClick={e => {
                          onSelectItem(media);
                          e.stopPropagation();
                        }}>
                        {selectedItems.find(m => m.id === media.id) ? (
                          <CheckCircleIcon color="#fff" width={24} height={24} />
                        ) : (
                          <CheckCircleEmptyIcon color="#fff" width={24} height={24} />
                        )}
                      </SelectIconWrapper>
                      <SelectIconWrapper
                        $notValid={true}
                        onClick={e => {
                          onEditItemClick(media);
                          e.stopPropagation();
                        }}>
                        <EditIcon color="#fff" />
                      </SelectIconWrapper>
                      {!!media.sellWithAi && <AiSellContainer>AI</AiSellContainer>}
                    </MediaWrapper>
                  </DraggableMediaItem>
                );
              })}
            </ListMedia>
          </Content>
        )
      )}
      {!!selectedItems.length && (
        <SelectedItemsOptions
          selectedItems={selectedItems}
          onUnselectClick={() => setSelectedItems([])}
          btnTitle="Remove"
          onBtnClick={onRemoveClick}
        />
      )}
      <PreviewAlbumMediaModal
        modalRef={previewAlbumMediaModalRef}
        type={selectedMedia?.kind}
        url={selectedMedia?.url}
        setSelectedUrl={setSelectedMedia}
      />
      <CreateAssetFolderModal
        modalRef={createAssetFolderModalRef}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        onSuccess={() => {}}
        influencerId={influencerId}
      />
      <EditAlbumMediaModal
        modalRef={editAlbumMediaModalRef}
        selectedMedia={selectedMedia}
        setSelectedMedia={setSelectedMedia}
        influencerId={influencerId}
        onSuccess={() => {
          dispatch(fetchAssetFolderById({ folderId, ...(influencerId && { influencerId }) }))
            .unwrap()
            .then(({ folder }) => {
              setSelectedFolder(folder);
            })
            .catch(err => {
              navigate(`${influencerId ? `/${influencerId}` : ''}/${VAULT}`);
              notifyError(err.message);
            });
        }}
      />
    </PageContainer>
  );
};

export default UploadedFolderPage;
